import z, { object } from 'zod';
import {
  InvoiceContactUpdateSchema,
  InvoiceEmailTimingSchema,
} from './invoice.types';

export const InvoiceTermsSettingsSchema = z.object({
  invoiceTermsActive: z.boolean(),
  invoiceTerms: z.string().optional(),
});

export type IInvoiceTermsSettingsSchema = z.infer<
  typeof InvoiceTermsSettingsSchema
>;

export interface IInvoiceTermsSettings {
  invoiceTermsActive: boolean;
  invoiceTerms: string;
}

// from GET /api/configurations/invoiceSettings
export enum InvoiceCreationEnum {
  AUTOMATIC = 'AUTOMATIC',
  MANUAL = 'MANUAL',
}

export const InvoiceCreationEnumZ = z.enum(InvoiceCreationEnum);

export enum FirstInvoiceCreationStrategyEnum {
  FOLLOW_EXISTING_SETTINGS = 'FOLLOW_EXISTING_SETTINGS',
  GENERATE_ON_QUOTE_PROCESSED = 'GENERATE_ON_QUOTE_PROCESSED',
}

export const FirstInvoiceCreationStrategyEnumZ = z.enum(
  FirstInvoiceCreationStrategyEnum,
);

export const InvoiceTemplateConfigSchema = z.object({
  showUsageWithoutEvents: z.boolean(),
  showOffering: z.boolean(),
  showPpu: z.boolean().default(false),
  showBillDate: z.boolean().default(false),
  showInvoiceDate: z.boolean().default(true),
  showCreatedDate: z.boolean().default(false),
  showNetTerms: z.boolean().default(false),
});

export type IInvoiceTemplateConfigSchema = z.infer<
  typeof InvoiceTemplateConfigSchema
>;

export interface IInvoiceTemplateConfigRes {
  showUsageWithoutEvents: boolean;
  showOffering: boolean;
  showPpu: boolean;
  showBillDate: boolean;
  showInvoiceDate: boolean;
  showCreatedDate: boolean;
  showNetTerms: boolean;
}

export enum AccountingDateCalculationStrategyEnum {
  BILLING_ANNIVERSARY = 'BILLING_ANNIVERSARY',
  CLOSEST_DATE_WITHIN_INVOICING_PERIOD = 'CLOSEST_DATE_WITHIN_INVOICING_PERIOD',
  CLOSEST_DATE_WITHIN_USAGE_PERIOD = 'CLOSEST_DATE_WITHIN_USAGE_PERIOD',
}
export const AccountingDateCalculationStrategyEnumZ = z.enum(
  AccountingDateCalculationStrategyEnum,
);

export const InvoiceAutomationSettingsSchema = object({
  invoiceCreation: InvoiceCreationEnumZ,
  invoiceDelay: z.number().min(-30).max(30),
  firstInvoiceCreationStrategy: FirstInvoiceCreationStrategyEnumZ,
  accountingDateCalculationStrategy: AccountingDateCalculationStrategyEnumZ,
});

export type IInvoiceAutomationSettings = z.infer<
  typeof InvoiceAutomationSettingsSchema
>;

export enum InvoiceContactEditBehaviorEnum {
  ENABLED = 'ENABLED',
  DISABLED = 'DISABLED',
  ENABLED_IF_PERIOD_OPEN = 'ENABLED_IF_PERIOD_OPEN',
}

export const InvoiceContactEditBehaviorEnumZ = z.enum(
  InvoiceContactEditBehaviorEnum,
);

export const InvoiceCustomizationSettingsSchema = z.object({
  invoiceContactEditBehavior: InvoiceContactEditBehaviorEnumZ,
});

export type IInvoiceCustomizationSettings = z.infer<
  typeof InvoiceCustomizationSettingsSchema
>;

export const InvoiceSettingsSchema = object({
  invoiceAutomation: InvoiceAutomationSettingsSchema,
  invoiceTerms: InvoiceTermsSettingsSchema,
  invoiceEmailingConfig: InvoiceEmailTimingSchema,
  invoiceTemplateConfig: InvoiceTemplateConfigSchema,
  invoiceCustomizationSettings: InvoiceCustomizationSettingsSchema.optional(),
});

export type IInvoiceSettings = z.infer<typeof InvoiceSettingsSchema>;
